import React, {useEffect, useState} from 'react';
import {Input, message, Modal, Tooltip} from "antd";
import {PencilSquareIcon, PlusIcon, TrashIcon} from "@heroicons/react/24/outline";
import { useTranslation } from 'react-i18next';
import axiosInstance from "../../axios/axiosInstance";
import {
   ActionButton,
   DataTable,
   PageCard,
   PageShell,
   PageToolbar,
   RowIconButton,
} from "../../components/ui";

function StoreCategory(props) {
   const { t } = useTranslation();

   const [categories, setCategories] = useState([]);
   const [isOpen, setIsOpen] = useState(false);
   const [modalMode, setModalMode] = useState('add'); // 'add' or 'edit'
   const [selectedCategory, setSelectedCategory] = useState(null);
   const [categoryName, setCategoryName] = useState('');


   const fetchCategories = async () => {
      try {
         const response = await axiosInstance.get('/financiers/categories');
         setCategories(response.data.data);
      } catch (error) {
         console.error('Error fetching categories:', error);
      }
   };

   useEffect(() => {
      fetchCategories();
   }, []);

   const openModal = (mode, category = null) => {
      setModalMode(mode);
      if (mode === 'edit' && category) {
         setSelectedCategory(category);
         setCategoryName(category.name);
      } else {
         setCategoryName('')
      }
      setIsOpen(true);
   };

   const closeModal = () => {
      setIsOpen(false);
      setSelectedCategory(null);
      setCategoryName('')
   };

   const deleteCategory = async (categoryId) => {
      try {
         await axiosInstance.delete(`/financiers/categories/${categoryId}`);
         message.success(t('store.deleted'));
      } catch (error) {
         console.error('Error deleting category:', error);
         message.error(t('store.deleteError'));
      }
      fetchCategories();
   };

   const addOrUpdateCategory = async () => {
      try {
         if (modalMode === 'add') {
            const response = await axiosInstance.post('/financiers/categories', {name: categoryName});
            setCategories([...categories, response.data]);
            message.success(t('store.added'));
         } else if (modalMode === 'edit' && selectedCategory) {
            await axiosInstance.put(`/financiers/categories/${selectedCategory.id}`, {name: categoryName});
            message.success(t('store.updated'));
         }
         closeModal();
         fetchCategories();
      } catch (error) {
         console.error('Error adding/updating category:', error);
         message.error(t('store.saveError'));
      }
   };

   const rowActions = (record) => (
       <div className="flex items-center gap-0.5">
          <Tooltip title={t('common.edit')}>
             <RowIconButton onClick={() => openModal('edit', record)}>
                <PencilSquareIcon className="w-[18px] h-[18px]"/>
             </RowIconButton>
          </Tooltip>
          <Tooltip title={t('common.delete')}>
             <RowIconButton className="hover:!text-red-600" onClick={() => deleteCategory(record.id)}>
                <TrashIcon className="w-[18px] h-[18px]"/>
             </RowIconButton>
          </Tooltip>
       </div>
   );

   const columns = [
      {
         title: t('common.id'),
         dataIndex: 'id',
         key: 'id',
         width: 80,
         render: (id) => <span className="text-xs text-slate-500 tabular-nums">{id}</span>,
      },
      {
         title: t('store.name'),
         dataIndex: 'name',
         key: 'name',
         render: (name) => <span className="text-sm font-medium text-[#1D2C7B]">{name}</span>,
      },
      {
         title: t('common.actions'),
         key: 'action',
         width: 96,
         render: (text, record) => rowActions(record),
      },
   ];

   const renderMobileCard = (record) => (
       <div className="flex items-start gap-3">
          <div className="min-w-0 flex-1">
             <p className="m-0 text-sm font-semibold text-[#1D2C7B] break-words">{record.name}</p>
             <span className="block text-xs text-slate-500 tabular-nums mt-0.5">ID: {record.id}</span>
          </div>
          {rowActions(record)}
       </div>
   );

   return (
       <PageShell>
          <PageCard>
             <PageToolbar
                 title={t('store.categories')}
                 count={categories.length}
                 actions={
                    <ActionButton icon={<PlusIcon className="w-4 h-4"/>} onClick={() => openModal('add')}>
                       {t('store.addNew')}
                    </ActionButton>
                 }
             />

             <DataTable
                 columns={columns}
                 dataSource={categories}
                 rowKey="id"
                 scrollX={520}
                 mobileCard={renderMobileCard}
                 pagination={{pageSize: 10, position: ['bottomCenter'], className: 'px-4 pb-3'}}
             />
          </PageCard>

          <Modal
              title={modalMode === 'add' ? t('store.addCategory') : t('store.editCategory')}
              open={isOpen}
              onCancel={closeModal}
              onOk={addOrUpdateCategory}
              okText={modalMode === 'add' ? t('store.add') : t('store.update')}
              width="100%"
              style={{maxWidth: 520}}
          >
             <Input
                 placeholder={t('store.namePh')}
                 value={categoryName}
                 onChange={(e) => setCategoryName(e.target.value)}
                 className="w-full"
                 style={{marginBottom: 16}}
             />
          </Modal>
       </PageShell>
   );
}

export default StoreCategory;
